import chroma from 'chroma-js'
import type { ICellRendererParams } from 'ag-grid-community'
import { parseISO } from 'date-fns'
import { IColorByValueAttributeMap } from 'app/services/api/colorByValue'

import tailwind from '_/tailwind.config'

type ICBVCellRendererGenerator = (
  cbvContainer: IColorByValueAttributeMap,
  isEnabled: boolean,
  // Enable composability of table cell renderers
  prevCellRenderer: ((params: ICellRendererParams) => JSX.Element) | undefined
) => (params: ICellRendererParams) => JSX.Element

const colorByValueCellRendererGenerator: ICBVCellRendererGenerator = (
  cbvContainer,
  isEnabled,
  prevCellRenderer
) => {
  return function ColorByValueRellRender(params: ICellRendererParams) {
    const colDef = params.colDef

    if (colDef?.colId === 'ag-Grid-AutoColumn') {
      // NOTE: AG Grid seem to pass cellrenderer of colDef corresponding
      //       to the value into colGroupDef which we will ignore.
      return params.valueFormatted ?? params.value
    }

    let el

    if (prevCellRenderer) {
      el = prevCellRenderer(params)
    } else {
      el = params.valueFormatted ?? params.value
    }

    if (colDef) {
      // This did not need to be in a cell renderer but we did
      // so to allow better composibility
      if (isEnabled) {
        colDef.cellStyle = (param) => {
          const bgColor = getColor(param.value, cbvContainer)

          const textColor =
            chroma(bgColor).luminance() > 0.3
              ? 'inherit'
              : tailwind.theme.colors.gray[100]

          return {
            color: textColor,
            background: bgColor
          }
        }
      } else {
        colDef.cellStyle = {}
      }
    }

    return <>{el}</>
  }
}

/**
 * Locate the color corresponding to value param in the color map object
 */
const getColor = (
  value: string | number | boolean,
  cbvContainer: IColorByValueAttributeMap
): string => {
  const colorMap = cbvContainer.configurations
  const firstValue = colorMap?.[0]?.value ?? {}
  let color = 'lightgray' // fallback

  if (firstValue.string != null) {
    const colorObj = colorMap.find((cm) => cm.value.string?.name === value)

    if (colorObj != null) {
      color = colorObj.color
    }
  }

  // Need to update this as per the requirement of whether to include minDate or maxDate in the range
  if (firstValue.date != null) {
    const timeToCompare = value
      ? parseISO(value?.toString().split('T')[0]).getTime()
      : null

    if (timeToCompare != null) {
      const colorObj = colorMap.find((cm) =>
        cm.value.date?.maxDate
          ? parseISO(cm.value.date.maxDate).getTime() >= timeToCompare
          : false
      )

      if (colorObj != null) {
        color = colorObj.color
      }
    }
  }

  if (firstValue.number != null) {
    /**
     * NOTE: We want "to" (max) range to be non inclusive unless no other
     *       range contains the value.
     *
     * For simplicity, with the assumpion that:
     * > The range options are declared from low to high order w/o dups
     *
     * We can simply do the match in reverse order such that the latter
     * declared "higher" range gets matched first.
     */
    const colorMapReversed = [...colorMap].reverse()

    const colorObj = colorMapReversed.find(
      (cm) =>
        cm.value.number != null &&
        Number(cm.value.number.min) <= Number(value) &&
        Number(cm.value.number.max) >= Number(value)
    )

    if (colorObj != null) {
      color = colorObj.color
    }
  }

  if (firstValue.boolean != null) {
    const colorObj = colorMap.find(
      (cm) =>
        cm.value.boolean === value ||
        String(cm.value.boolean) === String(value).toLowerCase()
    )

    if (colorObj != null) {
      color = colorObj.color
    }
  }

  return color
}

export { colorByValueCellRendererGenerator as cbvCellRendererGenerator }
